import { useEffect } from "react";

import { useGetPatientInteractionsActions } from "~/providers/hooks";
import { usePreviewStore } from "~/providers/stores/usePreviewStore";
import { getConsultationSummaryV2Data } from "~/providers/utils";
import { PatientIntakeConsultationSummaryV2 } from "~/providers/utils/types/patientIntakePreviewTypes";
import {
  PatientScreeningConsultationSummaryV2,
  SUMMARY_TYPE,
} from "~/providers/utils/types/patientPreviewTypes";
import { PatientPreviewTextInfo } from "../common";
import { FunctionalDomainsV2 } from "./functionalDomains";
import { HealthHistory } from "./HealthHistory";
import { PersonalLifeHistory } from "./PersonalLifeHistory";
import { PotentialAreasConcern } from "./PotentialAreasConcernV2";
import { PatientPreviewSelfReportedScoresV2 } from "./selfReportedScores";
import { PatientPreviewCurrentMedicationsV2 } from "./selfReportedScores/PatientPreviewCurrentMedicationsV2";
import { TreatmentRecommendations } from "./TreatmentRecommendations";

interface PreviewSectionV2Props {
  screeningSummary: PatientScreeningConsultationSummaryV2;
  patientId: string;
  intakeSummary?: PatientIntakeConsultationSummaryV2;
}

export const PreviewSectionsV2 = ({
  patientId,
  screeningSummary,
  intakeSummary,
}: PreviewSectionV2Props) => {
  const {
    screeningSummaryId,
    intakeSummaryId,
    isSummaryLiked,
    isTreatmentRecommendationsLiked,
    medications,
    scores,
    treatmentRecommendations,
    caseSummary,
    functionalDomains,
    healthHistory,
    personalLifeHistory,
    potentialAreasOfConcern,
    allergies,
  } = getConsultationSummaryV2Data({ screeningSummary, intakeSummary });

  const { showRateButtons, summaryType, handleRateSummary, isRatePending } =
    useGetPatientInteractionsActions({
      patientId,
      screeningSummaryId,
      intakeSummaryId,
    });

  const { patientId: storePatientId, setPatientId, reset } = usePreviewStore();

  useEffect(() => {
    if (!storePatientId || storePatientId != patientId) {
      reset();
      setPatientId(patientId);
    }
  }, [patientId, storePatientId, setPatientId, reset]);

  return (
    <>
      <PatientPreviewTextInfo
        showRateButtons={showRateButtons}
        title="Case summary"
        info={caseSummary}
        summaryType={summaryType}
        onRate={handleRateSummary}
        isLike={isSummaryLiked}
        isRatePending={isRatePending}
      />

      <PatientPreviewCurrentMedicationsV2
        medications={medications}
        allergies={allergies}
      />

      {healthHistory && <HealthHistory {...healthHistory} />}

      {personalLifeHistory && <PersonalLifeHistory {...personalLifeHistory} />}

      {functionalDomains && (
        <FunctionalDomainsV2 functionalDomains={functionalDomains} />
      )}

      {treatmentRecommendations && (
        <TreatmentRecommendations
          treatmentRecommendations={treatmentRecommendations}
          summaryType={SUMMARY_TYPE.treatment_recommendations}
          onRate={handleRateSummary}
          isLike={isTreatmentRecommendationsLiked}
          isRatePending={isRatePending}
        />
      )}

      <PotentialAreasConcern
        potentialAreasOfConcern={potentialAreasOfConcern}
      />

      <PatientPreviewSelfReportedScoresV2 scores={scores} />
    </>
  );
};
